import { skipToken } from '@reduxjs/toolkit/query';
import { createContext, useContext, useMemo } from 'react';

import { useCurrentUser, useHomeCards } from '@/hooks';

import type { MemberCoachBookingAuthority } from '@/features/appointments/memberCoachBookingState';
import type { DecodedBrowserAppointment } from '@/features/appointments/types';
import type { HomeCardPropsWithoutCallbacks } from '@/types';
import type { FC, ReactNode } from 'react';

import {
  resolveMemberCoachMatchState,
  resolveQueryAuthorityState,
} from '@/features/appointments/memberCoachBookingState';
import { useAppointmentClock } from '@/features/appointments/useNativeAppointmentRows';
import { useGetAppointmentsQuery } from '@/store/api/appointmentApi';

interface HomeCardsContextValue {
  appointments: DecodedBrowserAppointment[];
  appointmentState: MemberCoachBookingAuthority['appointmentState'];
  cards: HomeCardPropsWithoutCallbacks[];
  isLoading: boolean;
  now: number;
  matchStateForCoach: (
    coachId: string,
  ) => MemberCoachBookingAuthority['matchState'];
  onDismiss: (id: string) => void;
  refetch: () => void;
}

const HomeCardsContext = createContext<HomeCardsContextValue | null>(null);

/**
 * Owns the home dashboard-card reads for the member view.
 *
 * The full-width coach booking cards render directly under the greeting while
 * the "For you" carousel renders below the Moments block, so two sibling
 * components need the same card list. `useHomeCards` keeps optimistic dismissal
 * state in local `useState`, and `useAppointmentClock` runs an interval — calling
 * either twice would give the two blocks diverging card lists and a duplicate
 * timer. One provider keeps a single instance of both.
 *
 * Mounted only inside the client branch of `Home`, so coach sessions do not pay
 * for the surveys/events/sessions reads behind `useHomeCards`.
 */
export const HomeCardsProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const {
    currentData: currentUser,
    isError: isCurrentUserError,
    isFetching: isCurrentUserFetching,
    isSuccess: isCurrentUserSuccess,
    isUserLoading,
    user,
  } = useCurrentUser();
  const now = useAppointmentClock(!!user.id);
  const {
    currentData: appointmentPage,
    isError: isAppointmentsError,
    isFetching: isAppointmentsFetching,
    isSuccess: isAppointmentsSuccess,
  } = useGetAppointmentsQuery(user.id ? { userId: user.id } : skipToken);
  const {
    cards,
    isLoading: isCardsLoading,
    refetch,
    onDismiss,
  } = useHomeCards(now);

  const value = useMemo<HomeCardsContextValue>(() => {
    const appointmentState = resolveQueryAuthorityState({
      hasCurrentData: !!appointmentPage,
      isError: isAppointmentsError,
      isFetching: isAppointmentsFetching,
      isSuccess: isAppointmentsSuccess,
    });

    return {
      appointments: appointmentPage?.appointments ?? [],
      appointmentState,
      cards,
      isLoading: isCardsLoading || isUserLoading,
      matchStateForCoach: (coachId) =>
        resolveMemberCoachMatchState(
          {
            currentCoachIds:
              currentUser?.coaches.map((coach) => coach.id) ?? [],
            hasCurrentData: !!currentUser,
            isError: isCurrentUserError,
            isFetching: isCurrentUserFetching,
            isSuccess: isCurrentUserSuccess,
          },
          coachId,
        ),
      now,
      onDismiss,
      refetch,
    };
  }, [
    appointmentPage?.appointments,
    cards,
    currentUser,
    isAppointmentsError,
    isAppointmentsFetching,
    isAppointmentsSuccess,
    isCardsLoading,
    isCurrentUserError,
    isCurrentUserFetching,
    isCurrentUserSuccess,
    isUserLoading,
    now,
    onDismiss,
    refetch,
  ]);

  return (
    <HomeCardsContext.Provider value={value}>
      {children}
    </HomeCardsContext.Provider>
  );
};

export const useHomeCardsContext = (): HomeCardsContextValue => {
  const value = useContext(HomeCardsContext);
  if (!value) {
    throw new Error(
      'useHomeCardsContext must be used inside a HomeCardsProvider',
    );
  }
  return value;
};
